// src/services/emailService.js

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// Service d'envoi d'emails
const emailService = {
  // Envoyer un email de confirmation de rendez-vous
  sendConfirmationEmail: async (appointment, recipient) => {
    try {
      // Nettoyer l'objet appointment avant de l'envoyer
      const cleanAppointment = sanitizeAppointmentObject(appointment);
      
      // Appel à la fonction cloud avec les paramètres simplifiés
      const sendConfirmationEmailFn = httpsCallable(functions, 'sendConfirmationEmail');
      const result = await sendConfirmationEmailFn({ 
        appointment: cleanAppointment, 
        recipient 
      });
      
      console.log('Email de confirmation envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de confirmation:', error);
      throw error;
    }
  },

  // Envoyer un email d'annulation de rendez-vous
  sendCancellationEmail: async (appointment, recipient, cancelledBy, reason) => {
    try {
      // Nettoyer l'objet appointment avant de l'envoyer
      const cleanAppointment = sanitizeAppointmentObject(appointment);
      
      // Appel à la fonction cloud
      const sendCancellationEmailFn = httpsCallable(functions, 'sendCancellationEmail');
      const result = await sendCancellationEmailFn({ 
        appointment: cleanAppointment, 
        recipient, 
        cancelledBy, 
        reason 
      });
      
      console.log('Email d\'annulation envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email d\'annulation:', error);
      throw error;
    }
  },

  // Envoyer un email de rappel pour un rendez-vous
  sendReminderEmail: async (appointment) => {
    try {
      // Nettoyer l'objet appointment avant de l'envoyer
      const cleanAppointment = sanitizeAppointmentObject(appointment);
      
      // Appel à la fonction cloud
      const sendReminderEmailFn = httpsCallable(functions, 'sendReminderEmail');
      const result = await sendReminderEmailFn({ appointment: cleanAppointment });
      
      console.log('Email de rappel envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de rappel:', error);
      throw error;
    }
  },

  // Envoyer des rappels automatiques pour plusieurs rendez-vous
  sendBatchReminderEmails: async (appointments) => {
    try {
      const results = [];
      
      // Envoyer des rappels pour chaque rendez-vous individuellement
      for (const appointment of appointments) {
        try {
          const result = await emailService.sendReminderEmail(appointment);
          results.push({
            id: appointment.id,
            success: true,
            result
          });
        } catch (error) {
          console.error(`Erreur lors de l'envoi du rappel pour ${appointment.id}:`, error);
          results.push({
            id: appointment.id,
            success: false,
            error: error.message
          });
        }
      }
      
      return results;
    } catch (error) {
      console.error('Erreur lors de l\'envoi des rappels en lot:', error);
      throw error;
    }
  },

  // Envoyer un email de modification de rendez-vous
  sendAppointmentModificationEmail: async (appointment, recipient, modifiedBy, originalAppointment = null) => {
    try {
      // Nettoyer l'objet appointment avant de l'envoyer
      const cleanAppointment = sanitizeAppointmentObject(appointment);
      const cleanOriginalAppointment = originalAppointment ? sanitizeAppointmentObject(originalAppointment) : null;
      
      // Appel à la fonction cloud
      const sendModificationEmailFn = httpsCallable(functions, 'sendModificationEmail');
      const result = await sendModificationEmailFn({ 
        appointment: cleanAppointment, 
        recipient, 
        modifiedBy,
        originalAppointment: cleanOriginalAppointment
      });
      
      console.log('Email de modification envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email de modification:', error);
      throw error;
    }
  },
};

// Fonction utilitaire pour nettoyer les objets appointment
// Firebase Functions ne peuvent pas traiter les objets avec des champs non-sérialisables
function sanitizeAppointmentObject(appointment) {
  if (!appointment) return {};
  
  // Créer une copie de l'objet appointment
  const cleanedAppointment = { ...appointment };
  
  // Convertir les dates Firestore en chaînes de caractères
  if (cleanedAppointment.createdAt && typeof cleanedAppointment.createdAt.toDate === 'function') {
    cleanedAppointment.createdAt = cleanedAppointment.createdAt.toDate().toISOString();
  }
  
  if (cleanedAppointment.updatedAt && typeof cleanedAppointment.updatedAt.toDate === 'function') {
    cleanedAppointment.updatedAt = cleanedAppointment.updatedAt.toDate().toISOString();
  }
  
  if (cleanedAppointment.reminderSentAt && typeof cleanedAppointment.reminderSentAt.toDate === 'function') {
    cleanedAppointment.reminderSentAt = cleanedAppointment.reminderSentAt.toDate().toISOString();
  }
  
  // Si le praticien est inclus, nettoyer ses objets également
  if (cleanedAppointment.practitioner) {
    const cleanedPractitioner = { ...cleanedAppointment.practitioner };
    
    // Nettoyer les dates potentielles ici aussi
    if (cleanedPractitioner.createdAt && typeof cleanedPractitioner.createdAt.toDate === 'function') {
      cleanedPractitioner.createdAt = cleanedPractitioner.createdAt.toDate().toISOString();
    }
    
    if (cleanedPractitioner.updatedAt && typeof cleanedPractitioner.updatedAt.toDate === 'function') {
      cleanedPractitioner.updatedAt = cleanedPractitioner.updatedAt.toDate().toISOString();
    }
    
    cleanedAppointment.practitioner = cleanedPractitioner;
  }
  
  return cleanedAppointment;
}

export default emailService;